//
// # Perf Counters
//
// This file defines functions to capture perf start/end times
//

const liveUpdate = false; // Set true to update table dynamically

// Perf counter entry
//
export class PerfEntry {
  begin: number;
  count: number = 0;

  currentTime: number = 0;
  previousTime: number = 0;

  minTime: number = 0;
  maxTime: number = 0;

  totalTime: number = 0; // Sum of all times captured.
  totalDeviance: number = 0; // Sum of the jitter between frames.
  countHitches: number = 0; // Count of times the new value is off by more than 2x.

  row: HTMLTableRowElement;

  constructor(tag: string, row: HTMLTableRowElement) {
    this.row = row;
    row.insertCell(0).textContent = tag;
    row.insertCell(1);
    row.insertCell(2);
    row.insertCell(3);
    row.insertCell(4);
    row.insertCell(5);
    row.insertCell(6);
    row.insertCell(7);
    this.begin = performance.now();
  }

  // end calculates the time elapsed for this entry, and
  // accumulates the elapsed time into the tag's entry.
  end(): number {
    const now = performance.now();
    const diff = now - this.begin;
    if (this.count === 0) {
      // First time, set max/min to this value.
      this.currentTime = diff;
      this.previousTime = 0;
      this.minTime = diff;
      this.maxTime = diff;
      this.totalTime = diff;
      this.totalDeviance = 0;
    } else {
      this.previousTime = this.currentTime;
      this.currentTime = diff;
      this.totalDeviance += Math.abs(this.currentTime - this.previousTime);

      this.totalTime += diff;
      if (this.minTime > diff) this.minTime = diff;
      if (this.maxTime < diff) this.maxTime = diff;

      if (this.previousTime !== 0 && this.currentTime > 2 * this.previousTime) {
        this.countHitches++;
      }
    }
    this.count++;
    // Check if live update required.
    if (liveUpdate) {
      this.update();
    }
    return now;
  }

  // Update the table row text from the perf data.
  update() {
    // Update table row with new data
    this.row.cells[1].textContent = this.currentTime.toFixed(2);
    this.row.cells[2].textContent =
      this.count === 0 ? "-" : (this.totalTime / this.count).toFixed(2);
    this.row.cells[3].textContent =
      this.count === 0 ? "-" : (this.totalDeviance / this.count).toFixed(2);
    this.row.cells[4].textContent = this.minTime.toFixed(2);
    this.row.cells[5].textContent = this.maxTime.toFixed(2);
    this.row.cells[6].textContent = this.count.toString();
    this.row.cells[7].textContent = this.countHitches.toString();
  }

  // Zero this row's data
  clear() {
    this.begin = performance.now(); // Reset the begin time to now, needed for correct interval.
    this.count = 0;

    this.currentTime = 0;
    this.previousTime = 0;

    this.minTime = 0;
    this.maxTime = 0;

    this.totalTime = 0;
    this.totalDeviance = 0;
    this.countHitches = 0;
    this.update();
  }

  avg(): number {
    return this.count === 0 ? 0 : this.totalTime / this.count;
  }
}

// # Perf
//
// Perf contains the counters and methods for measuring event times.
export class Perf {
  perfMap = new Map<string, PerfEntry>();
  table = document.createElement("table");

  constructor() {
    this.table.style.backgroundColor = "black";
    this.table.style.color = "white";
    //this.table.style.border = "thick solid red";
    const th = this.table.createTHead();
    const thr = th.insertRow(0);
    const hnames: string[] = [
      "Tag",
      "Now MS",
      "Avg MS",
      "+/- MS",
      "Min",
      "Max",
      "Count",
      "Hitches",
    ];
    for (let i = 0; i < hnames.length; i++) {
      const thrc = thr.insertCell(i);
      thrc.textContent = hnames[i];
    }
  }

  // start captures the beginning of a timed event.
  // If it is the first time this tag has been seen, create
  // a new table row.
  // Return a reference to the entry so that end() does not
  // need to do a tag lookup.
  start = (tag: string): PerfEntry => {
    let e = this.perfMap.get(tag);
    if (e !== undefined) {
      e.begin = performance.now();
    } else {
      e = new PerfEntry(tag, this.table.insertRow(-1));
      this.perfMap.set(tag, e);
    }
    return e;
  };

  get = (tag: string): PerfEntry | undefined => this.perfMap.get(tag);

  interval(tag: string): PerfEntry {
    let e = this.perfMap.get(tag);
    if (e !== undefined) {
      e.begin = e.end(); // Immediately begin the next interval with the end time of the previous.
    } else {
      e = new PerfEntry(tag, this.table.insertRow(-1));
      this.perfMap.set(tag, e);
    }
    return e;
  }

  // update updates the table row data if liveUpdate is not configured.
  update = () => {
    if (!liveUpdate) {
      this.perfMap.forEach((value: PerfEntry) => {
        value.update();
      });
    }
  };

  // clear zeros out the counter values
  clear = () => {
    this.perfMap.forEach((value: PerfEntry) => {
      value.clear();
    });
  };
}
